if (typeof ProductQuantity !== 'function') {

  class ProductQuantity extends HTMLElement {

    constructor() {
      super();

      // -------------------------
      // CONFIGURATION
      // -------------------------
      this.pcb = parseInt(this.dataset.pcb, 10) || 1;
      this.variantId = parseInt(this.dataset.variantId, 10) || null;
      this.isInCart = !!this.querySelector('cart-product-quantity');

      const qty = this.querySelector('.qty-selector');
      const qtyMinus = this.querySelector('.qty-minus');
      const qtyPlus = this.querySelector('.qty-plus');

      if (!qty) return;

      // -------------------------
      // INPUT SETUP
      // -------------------------
      qty.min = this.pcb;
      qty.step = this.pcb;

      let qtyMax = parseInt(qty.getAttribute('max'), 10) || 999;
      let qtyMin = this.pcb;

      if (parseInt(qty.value, 10) < qtyMin) {
        qty.value = qtyMin;
      }

      // -------------------------
      // STOCK < PCB → BLOQUANT
      // -------------------------
      if (this.pcb > qtyMax) {
        this.disableAllControls(qty, qtyMinus, qtyPlus);
        this.updatePCBNotice(qtyMax);
        return;
      }

      // -------------------------
      // HANDLERS PDP UNIQUEMENT
      // -------------------------
      if (!this.isInCart) {

        if (qtyPlus) {
          qtyPlus.addEventListener('click', async (e) => {
            e.preventDefault();
            qty.value = parseInt(qty.value, 10) + this.pcb;
            await this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
          });
        }

        if (qtyMinus) {
          qtyMinus.addEventListener('click', async (e) => {
            e.preventDefault();
            qty.value = parseInt(qty.value, 10) - this.pcb;
            await this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
          });
        }

        // Sécurisation clavier
        qty.addEventListener('blur', async () => {
          const currentValue = parseInt(qty.value);

          if (currentValue % this.pcb !== 0) {
            const nextValue = Math.ceil(currentValue / this.pcb) * this.pcb;
            qty.value = nextValue;
          }

          await this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
        });

        // S'assurer que les quantités soient vérifiées quand la valeur du champ quantité change
        qty.addEventListener('input', async () => {
          // Utiliser un debounce pour éviter trop d'appels consécutifs
          clearTimeout(this._inputTimer);
          this._inputTimer = setTimeout(async () => {
            await this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
          }, 300);
        });
      }

      // -------------------------
      // ÉTAT INITIAL
      // -------------------------
      this.updatePCBNotice(qtyMax);

      if (!this.isInCart) {
        this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
      }

      // -------------------------
      // CHANGEMENT DE VARIANTE
      // -------------------------
      document.addEventListener('variant:changed', async (e) => {
        if (!e.detail?.variant) return;

        const newPCB = parseInt(
          e.detail.variant.metafields?.custom?.variant_pcb,
          10
        ) || 1;

        this.pcb = newPCB;
        this.dataset.pcb = newPCB;

        qtyMin = newPCB;
        qty.max = qtyMax = parseInt(qty.getAttribute('max'), 10) || 999;
        qty.min = newPCB;
        qty.step = newPCB;
        qty.value = newPCB;

        await this.validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax);
        this.updatePCBNotice(qtyMax);
      });
    }

    // -------------------------
    // PANIER
    // -------------------------
    async getCartQuantityForVariant(variantId) {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        const item = cart.items.find(i => i.variant_id === variantId);
        return item ? item.quantity : 0;
      } catch {
        return 0;
      }
    }

    // -------------------------
    // VALIDATION CENTRALE
    // -------------------------
    async validateQuantity(qty, qtyMinus, qtyPlus, qtyMin, qtyMax) {
      let cartQty = 0;

      if (this.variantId) {
        cartQty = await this.getCartQuantityForVariant(this.variantId);
      }

      // Max réellement commandable (PCB inclus)
      const effectiveMax =
        qtyMax - ((qtyMax - cartQty) % this.pcb) - cartQty;

      let value = parseInt(qty.value, 10) || qtyMin;

      // Clamp strict
      if (value < qtyMin) value = qtyMin;
      if (value > effectiveMax) value = effectiveMax;

      // S'assurer que la valeur entrée est un multiple de PCB
      if (value % this.pcb !== 0) {
        value = Math.ceil(value / this.pcb) * this.pcb;
      }

      qty.value = value;

      // Boutons
      if (qtyPlus) {
        qtyPlus.disabled = value + this.pcb > effectiveMax;
        qtyPlus.classList.toggle('disabled', qtyPlus.disabled);
      }

      if (qtyMinus) {
        qtyMinus.disabled = value - this.pcb < qtyMin;
        qtyMinus.classList.toggle('disabled', qtyMinus.disabled);
      }
    }

    // -------------------------
    // UI
    // -------------------------
    disableAllControls(qty, qtyMinus, qtyPlus) {
      [qty, qtyMinus, qtyPlus].forEach(el => {
        if (!el) return;
        el.disabled = true;
        el.classList.add('disabled');
      });
      this.disableAddToCartButton();
    }

    disableAddToCartButton() {
      const btn = this.closest('form')
        ?.querySelector('[data-js-product-add-to-cart]');
      if (!btn) return;

      btn.disabled = true;
      btn.classList.add('disabled');
      btn.setAttribute('aria-disabled', 'true');
    }

    enableAddToCartButton() {
      const btn = this.closest('form')
        ?.querySelector('[data-js-product-add-to-cart]');
      if (!btn) return;

      btn.disabled = false;
      btn.classList.remove('disabled');
      btn.removeAttribute('aria-disabled');
    }

    // -------------------------
    // PCB NOTICE
    // -------------------------
    updatePCBNotice(qtyMax) {
      const container = this.closest('.product__cart-functions');
      if (!container) return;

      this._removePCBNotice(container);

      // Si produit épuisé, ne rien afficher
      const addToCartBtn = container.querySelector('[data-js-product-add-to-cart]');
      if (addToCartBtn && addToCartBtn.classList.contains('disabled')) return;

      if (this.pcb > qtyMax) {
        this._createPCBNotice(
          container,
          "Stock insuffisant pour commander par PCB_PLACEHOLDER"
            .replace('PCB_PLACEHOLDER', this.pcb),
          true
        );
      } else if (this.pcb > 1) {
        this._createPCBNotice(
          container,
          "Commande par PCB_PLACEHOLDER minimum - Stock disponible = MAX_QTY_PLACEHOLDER bouteilles"
            .replace('PCB_PLACEHOLDER', this.pcb)
            .replace('MAX_QTY_PLACEHOLDER', qtyMax),
          false
        );
      }
    }

    _removePCBNotice(container) {
      const notice = container.querySelector('.pcb-notice');
      if (notice) notice.remove();
    }

    _createPCBNotice(container, message, disableButton) {
      const div = document.createElement('div');
      div.className = 'pcb-notice text-size--small';
      div.textContent = message;

      container.insertBefore(div, container.firstElementChild);

      disableButton
        ? this.disableAddToCartButton()
        : this.enableAddToCartButton();
    }
  }

  if (!customElements.get('product-quantity')) {
    customElements.define('product-quantity', ProductQuantity);
  }
}
